import { ProfileHeader } from "@components/profile-header";
import { Box } from "@mui-wrapper";
import { usePermission } from "@hooks/use-permission";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { BaseCompanyProps, CompanyTypeKind } from "@custom-types/base-types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { updateCompany } from "@store/workspace/workspace-thunks";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { WorkspaceTypeDropdown } from "@pages/workspace-profile/workspace-type-dropdown";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { blue, Chip, grey, Tooltip, useToast } from "@flat-ui";

/**
 * Display company information in the header of company profile page
 */
export function WorkspaceProfileHeader({
  company,
}: BaseCompanyProps): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandler();
  const { openToast } = useToast();
  const { isHbToSxgMigrated } = useAppSelector(companyStateSelector);
  const { isAllowed: isAllowedToChangeType, deniedReason } = usePermission(
    "workspace.changeType"
  );

  async function onChangeWorkspaceType(
    workspaceType: CompanyTypeKind
  ): Promise<void> {
    trackEvent({
      name: WorkspaceEvents.editWorkspaceDetails,
      props: {
        isNameChanged: false,
        type: workspaceType,
      },
    });

    try {
      await dispatch(
        updateCompany({
          companyId: company.id,
          payload: { type: workspaceType },
        })
      ).unwrap();
      openToast({
        title: "Workspace type updated successfully!",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `edit-workspace-type-${Date.now().toString()}`,
        title: "Failed to update workspace type",
        error,
      });
    }
  }

  const typeDropdown = (
    <WorkspaceTypeDropdown
      selectedType={company.type ?? "unspecified"}
      onTypeSelect={onChangeWorkspaceType}
      variant="underlined"
      isFullWidth={false}
      shouldShowLabel={false}
      isDisabled={!isAllowedToChangeType}
    />
  );

  return (
    <ProfileHeader
      type="COMPANY"
      name={company.name}
      nameAdornment={
        isHbToSxgMigrated ? (
          <Chip
            label="Migrated to Sphere XG"
            color={grey[0]}
            backgroundColor={blue[500]}
            textTransform="none"
            tooltip={
              "This workspace is migrated from HoloBuilder to Sphere XG, " +
              "meaning that all users in the workspace will use Sphere XG when they open any link pointing to " +
              "the workspace or any specific project. Users can still use the legacy UI if needed."
            }
          />
        ) : undefined
      }
      subheaderElement={
        isAllowedToChangeType ? (
          typeDropdown
        ) : (
          // The wrapper receives the hover events, because a disabled dropdown doesn't emit them.
          // It is inline-block so it is only as wide as the dropdown, which is where the tooltip anchors.
          <Tooltip title={deniedReason}>
            <Box component="span" tabIndex={0} sx={{ display: "inline-block" }}>
              {typeDropdown}
            </Box>
          </Tooltip>
        )
      }
    />
  );
}
